import React from 'react';
import '../../styles/subsection.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGithub, faLinkedin } from '@fortawesome/free-brands-svg-icons';
import { faRocket, faSearch, faHome, faGraduationCap } from '@fortawesome/free-solid-svg-icons';
import { useEffect } from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import { LightAsync as SyntaxHighlighter } from 'react-syntax-highlighter';
import { docco } from 'react-syntax-highlighter/dist/esm/styles/hljs';

// Import media files
import ticker_info from "../../media/ticker_info.mp4";
import ticker_match from "../../media/ticker_match.mp4";
import ticker_portfolio from "../../media/ticker_portfolio.mp4";
import ticker_pred from "../../media/ticker_pred.mp4";
import ticker_sentiment from "../../media/ticker_sentiment.mp4";

function AstroML() {
    useEffect(() => {
        document.title = 'AstroML';
        }, []);
    return (
        <div className="subsubsection-container">

            <div class="side-nav-container-astro">
                <aside className="subsubsection-side-nav-astro">
                    <a href="#frontend">Frontend</a>
                    <a href="#backend">Backend</a>
                    <a href="#MLOps">ML Ops</a>
                    <a href="#feature1">Prediction</a>
                    <a href="#feature2">Sentiment</a>
                    <a href="#feature3">Portfolio Risk</a>
                    <a href="#feature4">Function Matcher</a>
                    <a href="#feature5">Financials</a>
                    <a href="#current">Current</a>
                </aside>
            </div>

            <a className="back-to-index" href="/" aria-label="back to barinderthind.com" title="barinderthind.com">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 3 2 12h3v8h6v-5h2v5h6v-8h3L12 3z"/></svg>
            </a>


            <main className="subsubsection-content">
            <div className="titles">
                <h1>
                    <FontAwesomeIcon icon={faRocket} size="1x" /> Astro<span style={{ color: 'red' }}>ML</span>
                </h1>
                </div>

                <p className="subsubsection-paragraph">This app focused on providing machine learning based financial tools to users. There were a number of key features developed and I detail
them below. The entirety of the front-end and back-end was a solo effort and more details can be found in the next couple of sections.  </p>



                <section id="about" className="code-cleaned"> 
                    <h2>About</h2>

                    <section id="frontend" className="code-cleaned">
                    <h4>Frontend</h4>
                    <p className="subsubsection-paragraph">  The frontend of the AstroML web app is developed using React, a JavaScript library for building user interfaces. React's
                         component-based architecture enables modular, reusable UI components, facilitating efficient development and maintenance. Some key considerations I made: 
                            
                            <ul>
                            <li><strong>State Management:</strong> Utilized React's robust library of packages for handling user authentication, unit testing, theme 
                            settings (there was a dark mode haha), and financial data visualization.</li>
                            <li><strong>Responsive Design:</strong> Used the usual suite of flex and grid design frameworks to create a web and mobile friendly experience.</li>
                            <li><strong>Data Visualization:</strong> Integrated charting through Chart.js and D3.js to present financial data in an intuitive, interactive format, aiding in user
                             decision-making. Look for some videos below.</li>
                            <li><strong>Security:</strong> Implemented client-side security best practices, including data validation, secure API calls with environment variables, and handling sensitive information 
                            properly to protect user data.</li>
                            </ul>

                            This setup ensured a robust, scalable, and secure frontend.
                        
                        </p>
                    </section>

                    <section id="backend" className="code-cleaned">
                    <h4>Backend</h4>
                    <p className="subsubsection-paragraph">    The backend can be summarized as follows:

                    <ul>
                        <li><strong>Framework:</strong> Ruby Sinatra for simplicity and direct route handling.</li>
                            <li><strong>Database (Production):</strong> Amazon Redshift for handling large-scale data warehousing with high performance.</li>
                            <li><strong>Database (Development):</strong> PostgreSQL for local development, ensuring compatibility with Redshift.</li>
                    </ul>
                            
                            And some safety considerations taken: 
                            <ul>
                            <li><strong>Authentication/Authorization:</strong> Implemented to restrict access based on user roles and permissions.</li>
                            <li><strong>Input Sanitization:</strong> All user inputs are sanitized to prevent SQL injection and XSS attacks.</li>
                            <li><strong>Encrypted Sessions:</strong> Utilized to secure user sessions and prevent hijacking.</li>
                            <li><strong>HTTPS:</strong> Enforced for all transactions to ensure data encryption in transit.</li>
                            <li><strong>Security Audits:</strong> Conducted regularly alongside dependency updates to mitigate vulnerabilities.</li>
                            </ul>  </p>
                    </section>

                    <section id="MLOps" className="code-cleaned">
                    <h4>ML Ops</h4>
                    <p className="subsubsection-paragraph">Machine learning models were deployed through Flask. The Flask application was packaged up and uploaded to 
                    AWS Elastic Beanstalk where the API could be pinged every time a user triggered a particular feature. Here is an example of what the Flask code looked like for 
                    a simple display feature: 
                    
                    <SyntaxHighlighter language="python" style={docco} className="codeStyle_small">
{`from flask import Flask, request, jsonify
import pandas as pd
from yahooquery import Ticker
from flask_cors import CORS
import os

application = Flask(__name__)

allowed_origins = os.environ.get("ALLOWED_ORIGINS", "http://localhost:3000")
allowed_origins_list = [origin.strip() for origin in allowed_origins.split(",")]
CORS(application, origins=allowed_origins_list)


@application.route('/stock_display')
def stock_display():
    """
    Fetches historical stock data for the given ticker and timeframe using yahooquery and
    returns a dictionary containing the required columns (open, high, low, close, adjclose, volume)
    and the date in the format 'yyyy-mm-dd'. If an error occurs, an empty dictionary is returned.

    :param ticker: str, the stock ticker symbol
    :param timeframe: str, the desired timeframe to fetch historical data (e.g. '1d', '1mo', '1y')
    :return: dict, a dictionary containing the required columns and the date in the format 'yyyy-mm-dd'
    """

    # Arguments
    timeframe = request.args.get('timeframe').strip('{}')
    ticker = request.args.get('ticker').strip('{}')

    try:

        # API Call using yahooquery
        ticker_dat = Ticker(ticker)

        # Fetch historical data using specified timeframe
        historical_data = ticker_dat.history(period = timeframe)

        # Create a DataFrame with the required columns
        df = pd.DataFrame(historical_data, columns=['open', 'high', 'low', 'close', 'adjclose', 'volume'])

        # Adding in date
        df.reset_index(inplace=True)

        # Fixing date
        df['date'] = pd.to_datetime(df['date'], errors='coerce', utc=True)
        df['date'] = df['date'].dt.strftime('%Y-%m-%d')

        # Convert DataFrame to dictionary
        data_dict = {'data': df.to_dict('records')}

    except:

        data_dict = {}

    return jsonify(data_dict)

if __name__ == '__main__':
    application.run(host='0.0.0.0')`}
</SyntaxHighlighter>
                    
                    
                    </p>
                    </section>

                </section>

                <section id="features" className="code-cleaned">
                    <h2>Features</h2>
                    <p className="subsubsection-paragraph"> </p>

                    <section id="feature1" className="code-cleaned">
                    <h4>Stock Prediction</h4>
                    <p className="subsubsection-paragraph">Using Prophet (developed by Meta), with some careful considerations, a stock price predictor was developed and deployed. The primary objective of
                     leveraging the Prophet model in this application is to empower users with predictive insights into stock prices, taking into account both historical data and personal market sentiment. 
                     This approach is not about crunching numbers or applying a sophisticated algorithm; it's about making financial forecasting accessible, intuitive, and customizable for 
                     the end user. <br/> <br/>

                     At the heart of this feature lies the ability to tailor predictions according to user expectations about market conditions. By allowing users to input their own
                      sentiment—ranging from "neutral" to "great" or "terrible"—the model doesn't just offer a one-size-fits-all prediction. Instead, it provides a personalized forecast, 
                      integrating the user's perspective as a significant factor in the prediction process. This feature essentially places a "prior" on the model's output, adjusting the forecasts
                       to reflect the user's market outlook. The following is a video that showcases this feature: <br/><br/>

                            <video width="100%" controls autoplay>
                            <source src={ticker_pred} type="video/mp4" />
                            Your browser does not support the video tag.
                            </video>
                             </p>
                    </section>

                    <section id="feature2" className="code-cleaned">
                    <h4>Stock Sentiment</h4>
                    <p className="subsubsection-paragraph">Using APIs from Twitter, YouTube, and Reddit, I was able to provide a general sentiment of a particular ticker over time.
                        The sentiment was analyzed using HuggingFace transformers, namely BERT. The sentiment was updated in real time as new 
                        requests came in. You can watch the video below to get a sense of sentiment: <br/><br/>

                        <video width="100%" controls autoplay>
                                    <source src={ticker_sentiment} type="video/mp4" />
                                    Your browser does not support the video tag.
                                    </video> 
                                    
                                    </p>
                    </section>

                    <section id="feature3" className="code-cleaned">
                    <h4>Stock Portfolio Risk</h4>
                    <p className="subsubsection-paragraph">
                        
                        TThis features performs a detailed analysis of a portfolio of stocks, computing various financial metrics like variance, beta, alpha, the Sharpe ratio,
                     and correlations. It also compares the portfolio's performance to a benchmark (SPY ETF) by calculating the overall score, risk score, reward score, and tracking error.
                     These metrics include:

                            <ul>
                            <li>
                                <strong>Variance:</strong> The annualized variance of stock returns, indicating the dispersion from the mean.
                                <BlockMath math="\sigma^2 = \text{Var}(r) \times 252" />
                            </li>
                            <li>
                                <strong>Beta:</strong> Measures a stock's volatility relative to the market.
                                <BlockMath math="\beta = \frac{\text{Cov}(r, r_m)}{\text{Var}(r_m)}" />
                            </li>
                            <li>
                                <strong>Alpha:</strong> The excess return of a stock over the expected market returns.
                                <BlockMath math="\alpha = \overline{r} - (\beta \times \overline{r_m})" />
                            </li>
                            <li>
                                <strong>Sharpe Ratio:</strong> Represents the risk-adjusted return, quantifying the additional return per unit of risk.
                                <BlockMath math="\text{Sharpe Ratio} = \frac{\sqrt{252} \times (\overline{r} - r_f)}{\sigma}" />
                            </li>
                            <li>
                                <strong>Correlation:</strong> Assesses how stock returns move in relation to other stocks in the portfolio. This metric is particularly useful for understanding the 
                                diversification benefits within the portfolio.
                                {/* Assuming a generic formula for correlation; replace with specific calculation if needed */}
                                {/* <BlockMath math="\text{corr}(X, Y) = \frac{\text{Cov}(X, Y)}{\sigma_X \sigma_Y}" /> */}
                            </li>
                            </ul>

                            These metrics provide a comprehensive view of the portfolio's performance, risks, and potential for returns, facilitating informed investment decisions. The following is a 
                            video that showcases this feature: <br/><br/>

                                <video width="100%" controls autoplay>
                                    <source src={ticker_portfolio} type="video/mp4" />
                                    Your browser does not support the video tag.
                                    </video> 
                        </p>
                    </section>

                    <section id="feature4" className="code-cleaned">
                    <h4>Stock Function Matcher</h4>
                    <p className="subsubsection-paragraph">    <p>
                                The purpose of this feature was to analyze the historical performance of securities over a given period and identify all current securities that exhibit a similar trajectory to that of an initial reference security. This functionality enables users to discover stocks that are currently following a financial path akin to a particular stock in the past.
                                </p>

                                <p>
                                To achieve this, two distinct mathematical approaches were considered:
                                </p>

                                <ol>
                                <li>
                                    The first approach involved transforming the price series of each ticker into a relatively smooth function, a process executed in parallel across all tickers. The steps were as follows:
                                    <ul>
                                    <li>Transform each set of ticker prices into a smooth function using a method such as spline interpolation or moving averages.</li>
                                    <li>Compute the integral of the difference between these functions, after applying necessary pre-processing techniques to normalize or scale the data.</li>
                                    <li>Securities whose differenced functions yielded the smallest integrals were deemed most similar.</li>
                                    </ul>
                                    <p>The mathematical representation for this approach is given by:</p>
                                    <BlockMath math="\int |f(x) - g(x)| dx" /> where <InlineMath math="f(x)" /> and <InlineMath math="g(x)" /> represent the smooth functions derived from the ticker prices.
                                </li> <br/>

                                <li>
                                    The second approach involved converting the historical data of each security into an embedding, followed by the application of cosine similarity to assess likeness. 
                                    Despite the unconventional application of embeddings over a continuum (in this instance, time), this method proved logical as the aim was to facilitate comparisons within 
                                    identical time frames. Specifically, the comparison was drawn between day 1 of the reference security and the corresponding day of the securities under comparison, adjusted 
                                    for the time horizon.
                                </li>
                                </ol>

                                The following is a video that showcases this feature: <br/><br/>
                        <video width="100%" controls autoplay>
                            <source src={ticker_match} type="video/mp4" />
                            Your browser does not support the video tag.
                            </video> 
                        </p>
                    </section>

                    <section id="feature5" className="code-cleaned">
                    <h4>Stock Financials Summary</h4>
                    <p className="subsubsection-paragraph">Using Yahoo Finance's API, I was able to pull information on security financials and provide a concise and relevant summary to the 
                            users. This was helped the user experience by consolidating financial documents and organizing them in one place. 
                            The following is a video that showcases this feature: <br/><br/>

                            <video width="100%" controls autoplay>
                                <source src={ticker_info} type="video/mp4" />
                                Your browser does not support the video tag.
                                </video>
                            </p>
                    </section>

                </section>

                <section id="current" className="code-cleaned">
                    <h2>Current State</h2>

                    <p className="subsubsection-paragraph">Currently, AstroML is offline and the repository is private; however, if you wish to see 
                    the source code, please feel free to message me and I could run you through it! </p>
                </section>
                
            </main>
            
        </div>
    );
}

export default AstroML;
